import { useCallback } from 'react';

import { usePermissions } from '@/shared/permissions';

import { ECredentialScope, type TCredential } from '../types';
import { getCredentialScope } from '../utils';

/**
 * What the current user may do to a credential. Each action is checked
 * against the endpoint it calls, which follows the credential's scope.
 * Credentials managed by Kognitos allow nothing.
 *
 * @param organizationId - Current organization id.
 * @param workspaceId - Current workspace id.
 * @returns `canRename`, `canEditValues` and `canDelete`, each per credential.
 */
export function useCredentialPermissions(
  organizationId: string,
  workspaceId: string,
) {
  const { hasPermission } = usePermissions();

  const check = useCallback(
    (
      credential: TCredential,
      organizationKey: 'UpdateSecret' | 'AddSecretVersion' | 'DeleteSecret',
      workspaceKey: 'UpdateSecret2' | 'AddSecretVersion2' | 'DeleteSecret2',
    ) => {
      const scope = getCredentialScope(credential);
      if (scope === ECredentialScope.Organization) {
        return hasPermission([organizationKey], {
          organization: organizationId,
        });
      }
      return (
        scope === ECredentialScope.Workspace &&
        hasPermission([workspaceKey], {
          organization: organizationId,
          workspace: workspaceId,
        })
      );
    },
    [hasPermission, organizationId, workspaceId],
  );

  return {
    canRename: (credential: TCredential) =>
      check(credential, 'UpdateSecret', 'UpdateSecret2'),
    canEditValues: (credential: TCredential) =>
      check(credential, 'AddSecretVersion', 'AddSecretVersion2'),
    canDelete: (credential: TCredential) =>
      check(credential, 'DeleteSecret', 'DeleteSecret2'),
  };
}
